import { utils } from '@kurtosys/ksys-app-template';
import { computed, action } from 'mobx';
import { StoreContext } from '../../../configuration/StoreContext';
import { ICardProps } from '@kurtosys/ksys-app-components/dist/components/base/Card/models';
import { IGrid } from '@kurtosys/ksys-app-components/dist/components/base/Grid/models/IGrid';
import { IFactList } from '../../../models/app/IFactList';
import { IFactListProps } from '@kurtosys/ksys-app-components/dist/components/fact/FactList/models/IFactListProps';
import { IFactProps } from '@kurtosys/ksys-app-components/dist/components/fact/Fact/models/IFactProps';
import { IGridProps } from '@kurtosys/ksys-app-components/dist/components/base/Grid/models/IGridProps';
import { IBlankDataOptions } from '@kurtosys/ksys-app-components/dist/models/IBlankDataOptions';
import { AppStoreBase } from './AppStoreBase';
import { IAppComponents } from '../models/IAppComponents';
import { Manifest } from '../../../configuration/Manifest';

/* [Component: appStoreComponentImport] */

export class AppStore extends AppStoreBase {
	constructor(element: HTMLElement, url: string, storeContext: StoreContext, manifest: Manifest) {
		super(element, url, storeContext, manifest);
	}

	@computed
	get hasData(): boolean {
		let hasData = false;
		const factLists = this.activeFactListProps;
		const factListKeys = Object.keys(factLists);
		if (factListKeys.length > 0) {
			for (let i = 0; i < factListKeys.length; i++) {
				const factListKey = factListKeys[i];
				const listProps = factLists[factListKey];
				if (listProps && listProps.listBlankDataOptions) {
					const { showBlank } = listProps.listBlankDataOptions;
					if (showBlank === false) {
						const { items } = listProps;
						hasData = items && items.some(item => this.listItemHasData(item));
					}
					else {
						hasData = true;
					}
				}
				else {
					hasData = true;
				}
				if (hasData) {
					break;
				}
			}
		}
		return hasData;
	}

	@action
	contextsDidUpdateAfter = async () => {
		// TODO: Each Application should put custom logic here to handle changes to the data context
	}

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get cardProps(): ICardProps | undefined {
		const cardProps = this.appComponentConfiguration && this.appComponentConfiguration.cardProps;
		return this.mergeQueriesAndProps(cardProps);
	}

	@computed
	get gridProps(): IGridProps | undefined {
		const gridProps = this.appComponentConfiguration && this.appComponentConfiguration.gridProps;
		if (gridProps && gridProps.grid) {
			this.setGridContent(gridProps.grid);
		}
		if (gridProps && gridProps.breakpoints) {
			gridProps.breakpoints.forEach((breakpoint) => {
				if (breakpoint.props && breakpoint.props.grid) {
					this.setGridContent(breakpoint.props.grid);
				}
			});
		}
		return gridProps;
	}

	listItemHasData(item: IFactProps): boolean {
		let value;
		if (item.riskRatingProps) {
			value = item.riskRatingProps.value;
		}
		else if (item.morningstarRatingProps) {
			value = item.morningstarRatingProps.morningStarRating;
		}
		else {
			value = item.value;
		}
		return !utils.typeChecks.isNullOrEmpty(value);
	}

	@action
	setGridContent(grid: IGrid | undefined) {
		if (utils.typeChecks.isNullOrUndefined(grid)) {
			return grid;
		}
		for (const row of grid.rows) {
			for (const column of row.columns) {
				for (const component of column.components) {
					const { key = '', props } = component;
					const listKey = props && props.listKey || key;
					const listProps = utils.object.deepMergeObjects(props, this.getFactListProps(listKey)) as IFactListProps;
					let componentListProps;
					if (listProps && listProps.listBlankDataOptions) {
						const { showBlank } = listProps.listBlankDataOptions;
						if (showBlank === false) {
							const { items } = listProps;
							const listHasData = items && items.some(item => this.listItemHasData(item));
							componentListProps = listHasData ? listProps : { listBlankDataOptions: listProps.listBlankDataOptions };
						}
						else {
							componentListProps = listProps;
						}
					}
					else {
						componentListProps = listProps;
					}
					component.props = componentListProps;
				}
			}
		}
	}

	@computed
	get activeFactListProps(): { [key: string]: IFactListProps } {
		const activeFundLists: { [key: string]: IFactListProps } = {};

		const retrieveFundListsFromGrid = (grid: IGrid) => {
			for (const row of grid.rows) {
				for (const column of row.columns) {
					for (const component of column.components) {
						const { key = '', props } = component;
						const listKey: string = props && props.listKey || key;
						if (!activeFundLists[listKey]) {
							activeFundLists[listKey] = utils.object.deepMergeObjects(props, this.getFactListProps(listKey)) as IFactListProps;
						}
					}
				}
			}
		};

		if (this.appComponentConfiguration) {
			const { gridProps } = this.appComponentConfiguration;
			if (gridProps) {
				const { grid, breakpoints } = gridProps;
				if (grid) {
					retrieveFundListsFromGrid(grid);
				}
				else if (breakpoints) {
					breakpoints.forEach((breakpoint) => {
						const { props } = breakpoint;
						if (props) {
							const { grid } = props;
							if (grid) {
								retrieveFundListsFromGrid(grid);
							}
						}
					});
				}
			}
		}

		return activeFundLists;
	}

	getFactListProps(listKey: string): IFactListProps {
		const { items = [], ...list } = this.getList(listKey);
		const factListResult = this.mergeQueriesAndProps(list) as IFactListProps;
		const factPropItems = items.map(item => this.mergeQueriesAndProps(item) as IFactProps);
		const { itemBlankDataOptions, listBlankDataOptions, listValueSeparator } = factListResult;
		return {
			...factListResult,
			items: factPropItems,
			itemBlankDataOptions: { ...this.itemBlankDataOptions, ...itemBlankDataOptions },
			listBlankDataOptions: { ...this.listBlankDataOptions, ...listBlankDataOptions },
			listValueSeparator: listValueSeparator || this.listValueSeparator,
		};
	}

	getList(listKey: string): IFactList {
		const listConfig = this.listConfiguration.filter(config => config.key === listKey);
		if (listConfig.length > 0) {
			return listConfig[0];
		}

		const defaultList: IFactList = {
			key: listKey,
			props: {},
			queries: {},
			items: [],
		};

		return defaultList;
	}

	@computed
	get listConfiguration(): IFactList[] {
		if (this.configuration && this.configuration.components && this.configuration.components.factLists) {
			return this.configuration.components.factLists;
		}
		return [];
	}
	@computed
	get listValueSeparator(): string {
		if (this.configuration && this.configuration.components && this.configuration.components.listValueSeparator) {
			return this.configuration.components.listValueSeparator;
		}
		return ',';
	}
	@computed
	get listBlankDataOptions(): IBlankDataOptions {
		if (this.configuration && this.configuration.components && this.configuration.components.listBlankDataOptions) {
			return this.configuration.components.listBlankDataOptions;
		}
		return {};
	}
	@computed
	get itemBlankDataOptions(): IBlankDataOptions {
		if (this.configuration && this.configuration.components && this.configuration.components.itemBlankDataOptions) {
			return this.configuration.components.itemBlankDataOptions;
		}
		return {};
	}

	async customInitializeAfter() {

	}

}
